import Axios from 'axios'
import { Suspense } from 'react'
import { useSafeState, useMount, useLockFn } from 'ahooks'
import { App as AntDesignApp, ConfigProvider } from 'antd'
import { BrowserRouter as Router } from 'react-router-dom'

import keys from '@/config/keys'
import store from '@/redux/store'
import isEmpty from '@/utils/isEmpty'
import loadable from '@/utils/loadable'
import endpoints from '@/config/endpoints'
import { ELO } from '@/utils/loadableHelpers'
import sanitizeLink from '@/utils/sanitizeLink'
import { getAppSettings } from '@/utils/getAppSettings'
import { AntdAppProvider } from '@/context/antdAppContext'
import { setCurrentUser } from '@/redux/slices/authSlice'
import { resetThemeData } from '@/redux/slices/themeSlice'
import getAppRouteBasename from '@/utils/getAppRouteBasename'
import { getAntDThemeToken } from '@/utils/getAntDThemeToken'
import LoadingCenter from '@/components/Loading/LoadingCenter'
import { setParallaxProps } from '@/redux/slices/navigationSlice'
import { setIsAccountTabMenu } from '@/redux/slices/accountSlice'
import AppSkeletonBase from '@/components/Loading/AppSkeletonBase'
import { getAllQueryVariables, getQueryVariable } from '@/utils/getQueryVariable'

const COMMON_ERR_MSG = `Something went wrong. Please refresh & try again!`

const BaseView = loadable(() => import('@/BaseView'))

const AppBanner = loadable(() => import('@/components/micro/AppBanner'), ELO)
const CookieBanner = loadable(() => import('@/components/micro/CookieBanner'), ELO)
const HelmetFavicon = loadable(() => import('@/components/micro/HelmetFavicon'), ELO)

const Body = () => {
	const [error, setError] = useSafeState('')
	const [loading, setLoading] = useSafeState(true)
	const [checking, setChecking] = useSafeState(true)

	const qsVars = getAllQueryVariables() || {}

	return (
		<>
			<HelmetFavicon />
			<ConfigProvider theme={{ cssVar: true, hashed: false, token: getAntDThemeToken() }}>
				<AntDesignApp className='w-full' id={keys.APP_BODY_ID}>
					<AntdAppProvider>
						<BodyInner
							qsVars={qsVars}
							error={error}
							loading={loading}
							checking={checking}
							setError={setError}
							setLoading={setLoading}
							setChecking={setChecking}
						/>
					</AntdAppProvider>
				</AntDesignApp>
			</ConfigProvider>
		</>
	)
}

export default Body

const BodyInner = ({ loading, error, checking, setLoading, setError, setChecking, qsVars }) => {
	const [settings, setSettings] = useSafeState(undefined)
	const [terminologyLoading, setTerminologyLoading] = useSafeState(true)

	const getSettings = useLockFn(async () => {
		try {
			setLoading(true)
			const settingsData = await getAppSettings()
			if (settingsData) {
				setSettings(settingsData)

				const lastSegment = location.pathname.split('/').filter(Boolean).pop()
				const info = sanitizeLink(`/${lastSegment}`, { needInfo: true })
				if (info?.isModified) {
					const { url_root_path } = settingsData
					const src = `${window.location.origin}${url_root_path === '/' || !url_root_path ? '' : `/${url_root_path}`}${info?.link}`
					window.location.href = src
				}

				setLoading(false)
				return settingsData
			} else {
				setError(COMMON_ERR_MSG)
			}
		} catch (error) {
			setError(COMMON_ERR_MSG)
		}
	})

	const checkUserSession = async () => {
		try {
			setChecking(true)

			let ep = endpoints.user_profile
			const userToken = qsVars[keys.QUERY_PARAMS.USER_TOKEN]
			if (userToken) ep += `?${keys.QUERY_PARAMS.USER_TOKEN}=${userToken}`
			const { data: res } = await Axios.get(ep)

			console.log('App User Profile: ', res)
			store.dispatch(setCurrentUser(res?.user))

			const { redirect_route, base_url } = res || {}

			if (redirect_route) {
				const current_path = window.location.pathname

				const url = new URL(base_url)
				const root_path = url.pathname
				let src = `${window.location.origin}${root_path === '/' ? '' : root_path}${redirect_route}`
				const prefix = src.includes('?') ? '&' : '?'
				const qsArr = []

				// Attach '_redirect' query param that will contain the path that user is currently trying to go along with existing query params except user token
				if (
					!src.includes(`${keys.QUERY_PARAMS.REDIRECT}=`) &&
					!(keys.QUERY_PARAMS.REDIRECT in qsVars) &&
					current_path !== redirect_route
				) {
					const arr = []
					Object.keys(qsVars).forEach((key) => {
						if (key !== keys.QUERY_PARAMS.USER_TOKEN) arr.push(`${key}=${qsVars[key]}`)
					})
					const pure_current_path = current_path?.replace?.(root_path, '').trim?.()
					if (pure_current_path) {
						const val = pure_current_path + (!isEmpty(arr) ? `?${arr.join('&')}` : '')
						qsArr.push(`${keys.QUERY_PARAMS.REDIRECT}=${encodeURIComponent(val)}`)
					}
				}

				// Attach existing query params except user token
				Object.keys(qsVars).forEach((key) => {
					if (key !== keys.QUERY_PARAMS.USER_TOKEN) qsArr.push(`${key}=${qsVars[key]}`)
				})
				if (!isEmpty(qsArr)) src += `${prefix}${qsArr.join('&')}`

				/* Can't use react-router-dom as it's not yet instantiated */
				console.log('Redirecting to: ', src)
				window.location.href = src
			}
		} catch (error) {
			console.log('User needs to login.', error)
		} finally {
			setChecking(false)
		}
	}

	const getTerminology = async () => {
		try {
			const { data } = await Axios.get(endpoints.terminology, { withCredentials: false })
			console.log('App Terminology: ', data)
			window[keys._TERMINOLOGY_] = data
		} catch (error) {
			console.log('Terminology load error', error)
		} finally {
			setTerminologyLoading(false)
		}
	}

	const bootstrapApp = async () => {
		try {
			// Run in parallel
			await Promise.allSettled([getSettings(), checkUserSession(), getTerminology()])
		} catch (err) {
			console.log(err)
		}
	}

	useMount(() => {
		const browserUIHeight = window.outerHeight - window.innerHeight
		const actualViewportHeight =
			window.visualViewport?.height || window.innerHeight - browserUIHeight
		document.documentElement.style.setProperty(
			'--actual-viewport-height',
			`${actualViewportHeight}px`,
		)

		const VITE_NODE_ENV = import.meta.env.VITE_NODE_ENV
		console.log('Environment:', VITE_NODE_ENV)
		/* Disable console.log in prod */
		if (VITE_NODE_ENV === 'production') {
			const isDebugChecked = getQueryVariable('debug') || ''
			if (!isDebugChecked) console.log = () => {}
		}

		// Reset Redux Persisted State
		store.dispatch(resetThemeData({}))
		store.dispatch(setParallaxProps({}))
		store.dispatch(setIsAccountTabMenu(true))

		bootstrapApp()
	})

	if (loading && error) return <LoadingCenter msg={error} msgColor='tomato' simple={true} />

	const DEFAULT_LOADING_UI = <AppSkeletonBase />
	if (loading || checking || terminologyLoading) return DEFAULT_LOADING_UI

	const router_basepath = getAppRouteBasename(settings)

	return (
		<Router basename={router_basepath}>
			<main className='w-full'>
				<AppBanner />
				<Suspense fallback={DEFAULT_LOADING_UI}>
					<BaseView />
				</Suspense>
				<CookieBanner />
			</main>
		</Router>
	)
}
